import { MIN_PASSWORD_LENGTH } from '../auth/authApi.ts';
import { Field } from './Field.tsx';

export type NewPasswordFault = { field: 'password' | 'confirm'; message: string };

/**
 * The one check the browser makes before asking the server: length, then a match. The server
 * enforces the length rule itself (docs/specs/accounts.md, section 2). The confirm field exists
 * because no email service does: a mistyped new password costs the admin a hand-made reset link.
 */
export function newPasswordFault(password: string, confirm: string): NewPasswordFault | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { field: 'password', message: `Use at least ${MIN_PASSWORD_LENGTH} characters.` };
  }
  if (password !== confirm) return { field: 'confirm', message: 'The two passwords do not match.' };
  return null;
}

/** The DOM id of one of the pair, so a form can put focus on the field its fault names. */
export function newPasswordFieldId(idPrefix: string, field: NewPasswordFault['field']): string {
  return field === 'password' ? `${idPrefix}-new-password` : `${idPrefix}-confirm-password`;
}

/** The new-password pair, shared by the setup page and the account page. */
export function NewPasswordFields({
  idPrefix,
  password,
  confirm,
  fault,
  onPassword,
  onConfirm,
}: {
  idPrefix: string;
  password: string;
  confirm: string;
  fault: NewPasswordFault | null;
  onPassword: (value: string) => void;
  onConfirm: (value: string) => void;
}): JSX.Element {
  return (
    <>
      <Field
        id={newPasswordFieldId(idPrefix, 'password')}
        label="New password"
        type="password"
        autoComplete="new-password"
        required
        value={password}
        onChange={(e) => onPassword(e.target.value)}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        error={fault?.field === 'password' ? fault.message : null}
      />
      <Field
        id={newPasswordFieldId(idPrefix, 'confirm')}
        label="New password, again"
        type="password"
        autoComplete="new-password"
        required
        value={confirm}
        onChange={(e) => onConfirm(e.target.value)}
        error={fault?.field === 'confirm' ? fault.message : null}
      />
    </>
  );
}
